import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  TextField,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import Button from '../UIKit/Button';
import { lessonsAPI } from '../../services/api';
import { toast } from 'react-toastify';

const NotesContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  marginBottom: theme.spacing(2),
}));

const NotesHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  userSelect: 'none',
}));

const LessonNotesWidget = ({ lessonId, initialNotes = '' }) => {
  const { t } = useTranslation(['lessons']);
  const [isExpanded, setIsExpanded] = useState(false);
  const [notes, setNotes] = useState(initialNotes);
  const [isSaving, setIsSaving] = useState(false);

  // Update notes when initialNotes changes
  useEffect(() => {
    setNotes(initialNotes);
  }, [initialNotes]);

  const handleSaveNotes = async () => {
    if (!lessonId) {
      toast.error(t('lessonDetail.errors.saveNotes'));
      return;
    }

    setIsSaving(true);
    try {
      await lessonsAPI.saveNotes(lessonId, notes);
      toast.success(t('lessonDetail.success.notesSaved'));
    } catch (err) {
      console.error('Ошибка при сохранении заметок:', err);
      toast.error(t('lessonDetail.errors.saveNotes'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <NotesContainer>
      <NotesHeader onClick={() => setIsExpanded(!isExpanded)}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('lessonDetail.notes.myNotes')}
        </Typography>
        <IconButton size="small" sx={{ transition: 'transform 0.2s' }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </NotesHeader>

      <Collapse in={isExpanded}>
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <TextField
            fullWidth
            multiline
            rows={8}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('lessonDetail.notes.placeholder')}
            variant="outlined"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'background.default',
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveNotes}
            disabled={isSaving}
            fullWidth
            sx={{
              borderRadius: 2,
            }}
          >
            {isSaving ? t('common:actions.saving') : t('lessonDetail.notes.saveNotes')}
          </Button>
        </Box>
      </Collapse>
    </NotesContainer>
  );
};

export default LessonNotesWidget;
